import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ROLES = {
  BUSINESS_OWNER: 'business_owner',
  ACCOUNTANT: 'accountant',
  CLIENT: 'client',
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, [token]);

  /** The role string for the current user */
  const userRole = user?.role || null;

  /** Returns the default home path for a given role */
  const getHomeRoute = (role) => {
    if (role === ROLES.CLIENT) return '/client-portal';
    return '/dashboard';
  };

  /** Returns true if the user has one of the specified roles */
  const hasRole = (...roles) => {
    return roles.includes(userRole);
  };

  const signup = async (email, password, businessName) => {
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        email,
        password,
        business_name: businessName,
        role: ROLES.BUSINESS_OWNER,
      });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return { success: true, redirectTo: getHomeRoute(userData.role) };
    } catch (error) {
      let errorMsg = error.response?.data?.detail || error.message || 'Signup failed';
      if (Array.isArray(errorMsg)) {
        // Handle FastAPI/Pydantic validation errors
        errorMsg = errorMsg.map(err => {
          // e.g. "Value error, Password must contain at least one uppercase letter" -> "Password must contain at least one uppercase letter"
          return err.msg.replace('Value error, ', '');
        }).join('. ');
      } else if (typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg);
      }
      return { success: false, error: `Signup failed: ${errorMsg}` };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setToken(access_token);
      setUser(userData);
      return { success: true, redirectTo: getHomeRoute(userData.role) };
    } catch (error) {
      let errorMsg = error.response?.data?.detail || error.message || 'Login failed';
      if (Array.isArray(errorMsg)) {
        // Handle FastAPI/Pydantic validation errors
        errorMsg = errorMsg.map(err => {
          return err.msg.replace('Value error, ', '');
        }).join('. ');
      } else if (typeof errorMsg === 'object') {
        errorMsg = JSON.stringify(errorMsg);
      }
      return { success: false, error: `Login failed: ${errorMsg}` };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to send reset email' };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Reset failed' };
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, userRole,
      signup, login, logout, forgotPassword, resetPassword,
      hasRole, getHomeRoute,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};